import { useEffect, useState } from "react";
import type { AdminSession, CashClosureItem, CashClosureSnapshot } from "@/lib/api";
import { createCashClosure, getCashClosureSnapshot, listCashClosures } from "@/lib/api";
import { buildCashClosureReceiptHtml, downloadReceiptHtml, openPrintWindow } from "@/lib/receipt";

type Props = {
  open: boolean;
  onClose: () => void;
  session: AdminSession | null;
  currentUserName?: string | null;
};

type ModalTab = "current" | "history";

function todayLocal(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMoney(value: number): string {
  return `$${value.toLocaleString("es-CL")}`;
}

export default function CashClosureModal({ open, onClose, session, currentUserName }: Props) {
  const [tab, setTab] = useState<ModalTab>("current");
  const [businessDate, setBusinessDate] = useState(todayLocal());
  const [snapshot, setSnapshot] = useState<CashClosureSnapshot | null>(null);
  const [history, setHistory] = useState<CashClosureItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");

  async function loadData(date = businessDate): Promise<void> {
    if (!session) return;
    setLoading(true);
    setError("");
    try {
      const [currentSnapshot, closures] = await Promise.all([
        getCashClosureSnapshot(session, { date }),
        listCashClosures(session, { date, limit: 20 }),
      ]);
      setSnapshot(currentSnapshot);
      setHistory(closures);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar el arqueo");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !session) return;
    void loadData(businessDate);
  }, [open, session, businessDate]);

  if (!open) return null;

  function buildReceiptFromSnapshot(current: CashClosureSnapshot, closureCode: string, closedAt: string, closedByName?: string | null, receiptNotes?: string | null) {
    return buildCashClosureReceiptHtml({
      closureCode,
      businessDate: current.businessDate,
      closedAt,
      closedByName,
      notes: receiptNotes ?? null,
      totals: current.totals,
      byPaymentMethod: current.byPaymentMethod,
      byMovementType: current.byMovementType,
    });
  }

  function handlePrintCurrent(): void {
    if (!snapshot) return;
    const html = buildReceiptFromSnapshot(snapshot, `PREV-${businessDate.replace(/-/g, "")}`, new Date().toISOString(), currentUserName ?? null, notes || null);
    openPrintWindow(html);
  }

  function handleDownloadCurrent(): void {
    if (!snapshot) return;
    const html = buildReceiptFromSnapshot(snapshot, `PREV-${businessDate.replace(/-/g, "")}`, new Date().toISOString(), currentUserName ?? null, notes || null);
    downloadReceiptHtml(html, `arqueo-${businessDate}`);
  }

  function handlePrintHistory(item: CashClosureItem): void {
    const html = buildCashClosureReceiptHtml({
      closureCode: item.closureCode,
      businessDate: item.summary.businessDate ?? item.businessDate,
      closedAt: item.closedAt,
      closedByName: item.closedByName,
      notes: item.summary.notes ?? item.notes,
      totals: {
        payments: item.summary.totals?.payments ?? item.totalPayments,
        income: item.summary.totals?.income ?? item.totalIncome,
        expense: item.summary.totals?.expense ?? item.totalExpense,
        net: item.summary.totals?.net ?? item.netTotal,
      },
      byPaymentMethod: item.summary.byPaymentMethod ?? [],
      byMovementType: item.summary.byMovementType ?? [],
    });
    openPrintWindow(html);
  }

  function handleDownloadHistory(item: CashClosureItem): void {
    const html = buildCashClosureReceiptHtml({
      closureCode: item.closureCode,
      businessDate: item.summary.businessDate ?? item.businessDate,
      closedAt: item.closedAt,
      closedByName: item.closedByName,
      notes: item.summary.notes ?? item.notes,
      totals: {
        payments: item.summary.totals?.payments ?? item.totalPayments,
        income: item.summary.totals?.income ?? item.totalIncome,
        expense: item.summary.totals?.expense ?? item.totalExpense,
        net: item.summary.totals?.net ?? item.netTotal,
      },
      byPaymentMethod: item.summary.byPaymentMethod ?? [],
      byMovementType: item.summary.byMovementType ?? [],
    });
    downloadReceiptHtml(html, item.closureCode);
  }

  async function handleCloseCash(): Promise<void> {
    if (!session) return;
    setClosing(true);
    setError("");
    try {
      await createCashClosure(session, {
        date: businessDate,
        notes: notes.trim() || undefined,
      });
      setTab("history");
      setNotes("");
      await loadData(businessDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo registrar el cierre");
    } finally {
      setClosing(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/45 p-4">
      <div className="mx-auto flex h-full w-full max-w-6xl items-center justify-center">
        <div className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Arqueo de caja</h2>
              <p className="text-sm text-slate-500">Resumen diario de pagos manuales y movimientos de caja.</p>
            </div>
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-4">
            <div className="flex items-center gap-2 rounded-2xl bg-slate-100 p-1">
              <button
                onClick={() => setTab("current")}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${tab === "current" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}
              >
                Arqueo actual
              </button>
              <button
                onClick={() => setTab("history")}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${tab === "history" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}
              >
                Historial
              </button>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-slate-600">Fecha</label>
              <input
                type="date"
                value={businessDate}
                onChange={(e) => setBusinessDate(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {error ? (
            <div className="mx-6 mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {loading ? (
              <div className="flex h-56 items-center justify-center text-sm text-slate-500">Cargando arqueo...</div>
            ) : null}

            {!loading && tab === "current" && snapshot ? (
              <div className="space-y-5">
                <div className="grid gap-3 md:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-400">Pagos</div>
                    <div className="mt-2 text-2xl font-bold text-slate-800">{formatMoney(snapshot.totals.payments)}</div>
                    <div className="mt-1 text-xs text-slate-500">{snapshot.paymentsCount} registros</div>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <div className="text-xs uppercase tracking-wide text-emerald-600">Ingresos caja</div>
                    <div className="mt-2 text-2xl font-bold text-emerald-700">{formatMoney(snapshot.totals.income)}</div>
                    <div className="mt-1 text-xs text-emerald-600">{snapshot.byMovementType.find((item) => item.type === "ingreso")?.count ?? 0} movimientos</div>
                  </div>
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                    <div className="text-xs uppercase tracking-wide text-rose-600">Egresos caja</div>
                    <div className="mt-2 text-2xl font-bold text-rose-700">{formatMoney(snapshot.totals.expense)}</div>
                    <div className="mt-1 text-xs text-rose-600">{snapshot.byMovementType.find((item) => item.type === "egreso")?.count ?? 0} movimientos</div>
                  </div>
                  <div className="rounded-2xl border border-teal-200 bg-teal-50 p-4">
                    <div className="text-xs uppercase tracking-wide text-teal-600">Neto arqueo</div>
                    <div className="mt-2 text-2xl font-bold text-teal-700">{formatMoney(snapshot.totals.net)}</div>
                    <div className="mt-1 text-xs text-teal-600">Pagos + ingresos - egresos</div>
                  </div>
                </div>

                <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
                  <div className="space-y-5">
                    <div className="rounded-3xl border border-slate-200 bg-white">
                      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-800">Pagos por forma de pago</h3>
                          <p className="text-xs text-slate-400">Solo pagos manuales registrados en caja.</p>
                        </div>
                        <div className="text-xs text-slate-400">{snapshot.generatedAt ? new Date(snapshot.generatedAt).toLocaleString("es-CL") : ""}</div>
                      </div>
                      <div className="p-5">
                        {snapshot.byPaymentMethod.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">No hay pagos manuales para esta fecha.</div>
                        ) : (
                          <div className="space-y-3">
                            {snapshot.byPaymentMethod.map((item) => (
                              <div key={item.code} className="flex items-center justify-between rounded-2xl border border-slate-100 px-4 py-3">
                                <div>
                                  <div className="text-sm font-semibold text-slate-800">{item.name}</div>
                                  <div className="text-xs text-slate-400">{item.count} pagos</div>
                                </div>
                                <div className="text-sm font-bold text-slate-800">{formatMoney(item.amount)}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-100 px-5 py-4">
                        <h3 className="text-sm font-semibold text-slate-800">Detalle de pagos</h3>
                      </div>
                      <div className="max-h-72 overflow-auto">
                        <table className="min-w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-4 py-3">Hora</th>
                              <th className="px-4 py-3">Comprobante</th>
                              <th className="px-4 py-3">Contrato</th>
                              <th className="px-4 py-3">Metodo</th>
                              <th className="px-4 py-3 text-right">Monto</th>
                            </tr>
                          </thead>
                          <tbody>
                            {snapshot.payments.map((payment) => (
                              <tr key={payment.id} className="border-t border-slate-100">
                                <td className="px-4 py-3 text-slate-500">{new Date(payment.paidAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}</td>
                                <td className="px-4 py-3 font-medium text-slate-700">{payment.receiptNumber}</td>
                                <td className="px-4 py-3">
                                  <div className="font-medium text-slate-700">{payment.contractNumber}</div>
                                  <div className="text-xs text-slate-400">{payment.customerName}</div>
                                </td>
                                <td className="px-4 py-3 text-slate-500">{payment.paymentMethodName}</td>
                                <td className="px-4 py-3 text-right font-semibold text-slate-800">{formatMoney(payment.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {snapshot.payments.length === 0 ? (
                          <div className="px-4 py-8 text-center text-sm text-slate-400">Sin pagos manuales registrados.</div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div className="rounded-3xl border border-slate-200 bg-white p-5">
                      <h3 className="text-sm font-semibold text-slate-800">Registrar cierre</h3>
                      <p className="mt-1 text-sm text-slate-500">Guarda un snapshot del dia y lo deja disponible en el historial.</p>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={4}
                        placeholder="Notas del cierre (opcional)"
                        className="mt-4 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-teal-400"
                      />
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={handlePrintCurrent}
                          className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                        >
                          <i className="ri-printer-line mr-1.5" />Imprimir preview
                        </button>
                        <button
                          onClick={handleDownloadCurrent}
                          className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                        >
                          <i className="ri-download-line mr-1.5" />Descargar preview
                        </button>
                        <button
                          onClick={() => void handleCloseCash()}
                          disabled={closing}
                          className="ml-auto rounded-xl bg-teal-600 px-5 py-2 text-sm font-medium text-white transition hover:bg-teal-700 disabled:opacity-60"
                        >
                          {closing ? "Registrando..." : "Registrar cierre"}
                        </button>
                      </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-100 px-5 py-4">
                        <h3 className="text-sm font-semibold text-slate-800">Movimientos de caja</h3>
                      </div>
                      <div className="max-h-72 overflow-auto">
                        <table className="min-w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-4 py-3">Codigo</th>
                              <th className="px-4 py-3">Tipo</th>
                              <th className="px-4 py-3">Descripcion</th>
                              <th className="px-4 py-3 text-right">Monto</th>
                            </tr>
                          </thead>
                          <tbody>
                            {snapshot.movements.map((movement) => (
                              <tr key={movement.id} className="border-t border-slate-100">
                                <td className="px-4 py-3 font-medium text-slate-700">{movement.movementCode}</td>
                                <td className="px-4 py-3">
                                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${movement.movementType === "ingreso" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                    {movement.movementType}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="font-medium text-slate-700">{movement.description}</div>
                                  <div className="text-xs text-slate-400">{movement.category}</div>
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-slate-800">{formatMoney(movement.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {snapshot.movements.length === 0 ? (
                          <div className="px-4 py-8 text-center text-sm text-slate-400">Sin movimientos de caja para esta fecha.</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {!loading && tab === "history" ? (
              <div className="space-y-4">
                {history.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-200 px-6 py-12 text-center text-sm text-slate-400">No hay cierres registrados para esta fecha.</div>
                ) : (
                  history.map((item) => (
                    <div key={item.id} className="rounded-3xl border border-slate-200 bg-white p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-800">{item.closureCode}</h3>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                              {new Date(item.closedAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-slate-500">
                            {new Date(item.businessDate).toLocaleDateString("es-CL")} · {item.closedByName ?? "Sistema"}
                          </div>
                          {item.notes ? <div className="mt-2 text-sm text-slate-600">{item.notes}</div> : null}
                        </div>

                        <div className="grid gap-2 text-right sm:grid-cols-4 lg:min-w-[420px]">
                          <div className="rounded-2xl bg-slate-50 px-4 py-3">
                            <div className="text-xs text-slate-400">Pagos</div>
                            <div className="text-sm font-bold text-slate-800">{formatMoney(item.totalPayments)}</div>
                          </div>
                          <div className="rounded-2xl bg-emerald-50 px-4 py-3">
                            <div className="text-xs text-emerald-600">Ingresos</div>
                            <div className="text-sm font-bold text-emerald-700">{formatMoney(item.totalIncome)}</div>
                          </div>
                          <div className="rounded-2xl bg-rose-50 px-4 py-3">
                            <div className="text-xs text-rose-600">Egresos</div>
                            <div className="text-sm font-bold text-rose-700">{formatMoney(item.totalExpense)}</div>
                          </div>
                          <div className="rounded-2xl bg-teal-50 px-4 py-3">
                            <div className="text-xs text-teal-600">Neto</div>
                            <div className="text-sm font-bold text-teal-700">{formatMoney(item.netTotal)}</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => handlePrintHistory(item)}
                          className="rounded-xl bg-teal-50 px-4 py-2 text-sm font-medium text-teal-700 transition hover:bg-teal-100"
                        >
                          <i className="ri-printer-line mr-1.5" />Imprimir
                        </button>
                        <button
                          onClick={() => handleDownloadHistory(item)}
                          className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                        >
                          <i className="ri-download-line mr-1.5" />Descargar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
